import { ReactElement } from 'react'
import { OpeningForm } from '@structuralPlanningComponents'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useEditElementStore } from '@editorStores'
import { useIfcMode } from '@editorHooks'
import useIFCImportGuided from '../../hooks/useIFCImportGuided'

interface Props {
  wallGuid: string | null
  openingGuid: string
  onDeselect: () => void
}

/**
 * Edits the coordinates of a single opening selected in the IFC importer, reusing Structural
 * Planning's `OpeningForm`. Selection is owned by the caller (the align panel's multi-selection);
 * saving or deselecting clears it. "in Draufsicht bearbeiten" switches to the importer's own
 * `draw-openings` tool (`IFCOpeningDrawPanel`/`useIFCImportOpeningDrawing`), the counterpart of
 * Structural Planning's top-view wall editing — offered only outside the guided flow, since
 * `draw-openings` is a plain `ifcMode` value and switching to it would silently exit `guided`
 * (`ifcMode` holds exactly one value at a time); the guided "Öffnungen ausrichten" step has no
 * top-view variant of its own.
 */
const IFCOpeningEditForm = ({ wallGuid, openingGuid, onDeselect }: Props): ReactElement => {
  const isOrthographic = useCameraStore(state => state.isOrthographic)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const { setIfcMode } = useIfcMode()
  const { isGuidedActive } = useIFCImportGuided()

  return (
    <OpeningForm
      selectedWallId={wallGuid}
      selectedOpeningId={openingGuid}
      deselectWall={onDeselect}
      setElements={onDeselect}
      mode={isOrthographic ? '2D' : '3D'}
      toggleMode={
        isGuidedActive
          ? undefined
          : () => {
              if (!wallGuid) return
              setActiveElement(wallGuid)
              setIfcMode('draw-openings')
            }
      }
    />
  )
}

export default IFCOpeningEditForm
